import PerformerAssetStep from "./register/PerformerAssetStep.jsx";
import { performerText } from "./register/performerCatalog.js";
import { validatePerformerAsset } from "./register/performerValidation.js";
import { performerPayload } from "./register/performerPayload.js";
import { resolveRegistrationOutcome } from "./register/registrationOutcome.js";
import PerformerRegistrationComplete from "./register/PerformerRegistrationComplete.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerDriver, uploadDriverPublicVehiclePhoto } from "../api/auth";
import LanguageSelector from "./LanguageSelector.jsx";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import DriverTelegramPhoneVerification from "./DriverTelegramPhoneVerification.jsx";
import LegalDocsModal from "./LegalDocsModal.jsx";
import {
  clearDriverRegistrationResumeState,
  readDriverPhoneVerificationState,
  readDriverRegistrationDraft,
  saveDriverRegistrationDraft,
} from "../utils/driverPhoneVerificationState.js";
import {
  clearDriverRegistrationVehiclePhotoCheckpoint,
  readDriverRegistrationVehiclePhotoCheckpoint,
  saveDriverRegistrationVehiclePhotoCheckpoint,
} from "../utils/driverRegistrationPhotoCheckpoint.js";
import {
  fetchUzLegalBundle,
  findLegalDocument,
  legalDocumentUrl,
} from "../services/legalV5.js";
import {
  DRIVER_WORK_COUNTRIES as WORK_COUNTRIES,
  normalizeDriverWorkCountriesSelection,
} from "../utils/driverWorkCountriesCatalog.js";

// REISOR_STAGE123_DRIVER_REGISTRATION_SIMPLE_ONBOARDING_V1
// REISOR_STAGE142_DRIVER_GUEST_REGISTRATION_UX_V1
const CARRIER_STATUSES = [
  { value: "ip", key: "driverCarrierStatus.ip", fallback: "ИП" },
  { value: "self_employed", key: "driverCarrierStatus.self_employed", fallback: "Самозанятый" },
  { value: "company", key: "driverCarrierStatus.company", fallback: "ООО / компания" },
  { value: "park_logist", key: "driverCarrierStatus.park_logist", fallback: "Парк / логист" },
];

const STEPS = ["person", "contacts", "body", "countries", "access"];

const statusNeedsInn = (status) => ["ip", "self_employed", "company"].includes(String(status || ""));
const statusNeedsName = (status) => ["company", "park_logist"].includes(String(status || ""));

function phoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizePhoneInput(value) {
  const raw = String(value || "");
  const digits = raw.replace(/\D/g, "").slice(0, 15);
  return raw.trim().startsWith("+") ? `+${digits}` : digits;
}

export default function Register({ onCancel, onRegistered }) {
  const { t, i18n } = useTranslation();

  const tr = (key, fallback) => {
    try {
      const v = t(key, { defaultValue: fallback });
      return typeof v === "string" && v ? v : fallback;
    } catch {
      return fallback;
    }
  };

  const phase3Text = (ru, uz, trText) =>
    performerText(
      i18n?.language || "ru",
      ru,
      uz,
      trText
    );

  const restoredDraft = useMemo(() => readDriverRegistrationDraft(), []);
  const [step, setStep] = useState(() => {
    const value = Number(restoredDraft?.step || 0);
    return Number.isInteger(value) && value >= 0 && value < STEPS.length ? value : 0;
  });
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => ({
    lastName: String(restoredDraft?.form?.lastName || ""),
    firstName: String(restoredDraft?.form?.firstName || ""),
    middleName: String(restoredDraft?.form?.middleName || ""),
    phoneMain: String(restoredDraft?.form?.phoneMain || ""),
    phoneExtra: String(restoredDraft?.form?.phoneExtra || ""),
    carrierStatus: String(restoredDraft?.form?.carrierStatus || ""),
    inn: String(restoredDraft?.form?.inn || ""),
    carrierName: String(restoredDraft?.form?.carrierName || ""),
    car: String(restoredDraft?.form?.car || ""),
    carYear: String(restoredDraft?.form?.carYear || ""),
    carNumber: String(restoredDraft?.form?.carNumber || ""),
    password: "",
    password2: "",
    performerCategory: String(restoredDraft?.form?.performerCategory || ""),
    assetType: String(restoredDraft?.form?.assetType || ""),
    seats: String(restoredDraft?.form?.seats ?? ""),
    passengerServices: Array.isArray(restoredDraft?.form?.passengerServices)
      ? restoredDraft.form.passengerServices
          .map((value) => String(value || "").toUpperCase())
          .filter(Boolean)
      : [],
    bodyType: String(restoredDraft?.form?.bodyType || ""),
    bodyTypeOther: String(restoredDraft?.form?.bodyTypeOther || ""),
    capacityTons: String(restoredDraft?.form?.capacityTons ?? ""),
    volumeM3: String(restoredDraft?.form?.volumeM3 ?? ""),
    workCountries: normalizeDriverWorkCountriesSelection(
      restoredDraft?.form?.workCountries,
      { defaultWhenEmpty: false }
    ),
  }));
  const [phoneVerification, setPhoneVerification] = useState(() => readDriverPhoneVerificationState());
  const [vehiclePhotoFile, setVehiclePhotoFile] = useState(null);
  const [vehiclePhotoPreview, setVehiclePhotoPreview] = useState("");
  const [registrationCheckpoint, setRegistrationCheckpoint] = useState(() =>
    readDriverRegistrationVehiclePhotoCheckpoint()
  );

  const [loading, setLoading] = useState(false);
  const [registrationOutcome, setRegistrationOutcome] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [personalDataConsent, setPersonalDataConsent] = useState(false);
  const [currentLegalBundle, setCurrentLegalBundle] = useState(null);
  const [selectedLegalDoc, setSelectedLegalDoc] = useState(null);
  const personRefs = useRef({ lastName: null, firstName: null, middleName: null });

  useEffect(() => {
    let active = true;
    fetchUzLegalBundle("driver", i18n?.language || "ru")
      .then((bundle) => { if (active) setCurrentLegalBundle(bundle); })
      .catch(() => { if (active) setCurrentLegalBundle(null); });
    return () => { active = false; };
  }, [i18n?.language]);

  useEffect(() => {
    if (!vehiclePhotoFile) {
      setVehiclePhotoPreview("");
      return undefined;
    }

    const url = URL.createObjectURL(vehiclePhotoFile);
    setVehiclePhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [vehiclePhotoFile]);

  function selectVehiclePhoto(file) {
    setError("");
    if (!file) {
      setVehiclePhotoFile(null);
      return;
    }

    if (!String(file.type || "").toLowerCase().startsWith("image/")) {
      setError(tr("registerWizard.errors.vehiclePhotoType", "Выберите изображение транспорта"));
      return;
    }

    if (Number(file.size || 0) > 25 * 1024 * 1024) {
      setError(tr("registerWizard.errors.vehiclePhotoSize", "Фото транспорта должно быть не больше 25 МБ"));
      return;
    }

    setVehiclePhotoFile(file);
  }

  const persistRegistrationDraft = () => {
    const { password: _password, password2: _password2, ...safeForm } = form;
    saveDriverRegistrationDraft({ step, form: safeForm, savedAt: new Date().toISOString() });
  };

  useEffect(() => {
    persistRegistrationDraft();
    // Persist only non-secret onboarding state for Telegram cold-start return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    form.lastName,
    form.firstName,
    form.middleName,
    form.phoneMain,
    form.phoneExtra,
    form.performerCategory,
    form.assetType,
    form.seats,
    form.passengerServices,
    form.bodyType,
    form.bodyTypeOther,
    form.capacityTons,
    form.volumeM3,
    form.workCountries,
  ]);

  const legalDoc = (documentNumber) => findLegalDocument(currentLegalBundle, documentNumber);
  const openLegalDoc = (documentNumber) => {
    const doc = legalDoc(documentNumber);
    if (!doc) {
      setError(tr("registerWizard.errors.legalLoad", "Не удалось загрузить текущие юридические документы REISOR. Повторите попытку."));
      return;
    }
    setSelectedLegalDoc(doc);
  };

  const inputStyle = useMemo(
    () => ({
      width: "100%",
      padding: 14,
      marginBottom: 10,
      borderRadius: 16,
      border: "1px solid #334155",
      background: "rgba(30, 41, 59, 0.9)",
      color: "white",
      fontSize: 16,
    }),
    []
  );

  const labelStyle = {
    fontSize: 13,
    color: "#cbd5e1",
    fontWeight: 800,
    margin: "8px 0 7px",
  };

  const hintStyle = {
    color: "#94a3b8",
    fontSize: 12,
    lineHeight: 1.45,
    margin: "-2px 2px 12px",
  };

  const bodyTypeLabel = (value) =>
    t(`driverOptions.bodyTypes.${value}`, { defaultValue: value });

  const ch = (key) => (event) => {
    let value = event.target.value;

    if (key === "phoneMain" || key === "phoneExtra") value = normalizePhoneInput(value);
    if (key === "inn") value = value.replace(/\D/g, "").slice(0, 12);
    if (key === "carrierName") value = value.slice(0, 160);
    if (key === "car") value = value.slice(0, 120);
    if (key === "carYear") value = value.replace(/\D/g, "").slice(0, 4);
    if (key === "carNumber") value = value.toUpperCase().slice(0, 32);
    if (key === "password" || key === "password2") value = value.replace(/\D/g, "").slice(0, 6);

    setError("");
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  function readPersonSnapshot() {
    const read = (key) => {
      const domValue = personRefs.current?.[key]?.value;
      const value = domValue == null ? form[key] : domValue;
      return String(value || "").trim();
    };
    return {
      lastName: read("lastName"),
      firstName: read("firstName"),
      middleName: read("middleName"),
    };
  }

  function validateStep(index, candidate = form) {
    const key = STEPS[index];

    if (key === "person") {
      if (!String(candidate.lastName || "").trim()) return tr("registerWizard.errors.lastName", "Укажите фамилию");
      if (!String(candidate.firstName || "").trim()) return tr("registerWizard.errors.firstName", "Укажите имя");
    }

    if (key === "contacts") {
      const main = phoneDigits(form.phoneMain);
      const verifiedMain = phoneDigits(phoneVerification?.phoneNormalized || form.phoneMain);
      const extra = phoneDigits(form.phoneExtra);
      if (main.length < 7 || main.length > 15) {
        return tr("registerWizard.errors.telegramPhone", "Укажите номер телефона, привязанный к Telegram");
      }
      if (extra && (extra.length < 7 || extra.length > 15)) {
        return tr("registerWizard.errors.extraPhone", "Проверьте дополнительный номер телефона");
      }
      if (extra && extra === verifiedMain) {
        return tr("registerWizard.errors.phoneSame", "Дополнительный номер должен отличаться от Telegram-номера");
      }
      const proofMatchesInput =
        !phoneVerification?.inputPhone ||
        phoneDigits(phoneVerification.inputPhone) === main;
      if (
        String(phoneVerification?.status || "").toUpperCase() !== "VERIFIED" ||
        !proofMatchesInput
      ) {
        return tr("registerWizard.errors.telegramConfirmation", "Подтвердите основной номер через Telegram");
      }
    }

    if (key === "carrier") {
      if (!form.carrierStatus) return tr("registerWizard.errors.carrierStatus", "Выберите статус работы");
      if (statusNeedsInn(form.carrierStatus) && !String(form.inn || "").trim()) {
        return tr("registerWizard.errors.inn", "Укажите ИНН");
      }
      if (statusNeedsName(form.carrierStatus) && !String(form.carrierName || "").trim()) {
        return tr("registerWizard.errors.carrierName", "Укажите название компании, парка или логиста");
      }
    }

    if (key === "vehicle") {
      if (!String(form.car || "").trim()) return tr("registerWizard.errors.car", "Укажите марку и модель автомобиля");
      if (!/^\d{4}$/.test(String(form.carYear || ""))) {
        return tr("registerWizard.errors.carYear", "Укажите год выпуска четырьмя цифрами");
      }
      if (!String(form.carNumber || "").trim()) return tr("registerWizard.errors.carNumber", "Укажите госномер автомобиля");
    }

    if (key === "body") {
      const performerError =
        validatePerformerAsset(
          form,
          {
            tr,
            language:
              i18n?.language || "ru",
          }
        );

      if (performerError) {
        return performerError;
      }

      if (
        String(candidate.performerCategory || "").toUpperCase() === "CARGO" &&
        !vehiclePhotoFile &&
        !registrationCheckpoint?.vehiclePhotoUploaded
      ) {
        return tr(
          "registerWizard.errors.vehiclePhoto",
          "Добавьте фото транспорта"
        );
      }
    }

    if (key === "countries") {
      if (!Array.isArray(form.workCountries) || !form.workCountries.length) {
        return tr("registerWizard.errors.workCountries", "Выберите хотя бы одну страну, которая вас интересует для новых грузов");
      }
    }

    if (key === "access") {
      if (String(form.password || "").length !== 6) {
        return tr("registerWizard.errors.pin", "Придумайте PIN-код из 6 цифр");
      }
      if (form.password !== form.password2) {
        return tr("registerWizard.errors.pinMismatch", "PIN-коды не совпадают");
      }
      if (!agreementAccepted) {
        return tr("registerWizard.errors.offerRequired", "Примите пользовательское соглашение и правила REISOR");
      }
      if (!personalDataConsent) {
        return tr("registerWizard.errors.pdConsentRequired", "Дайте отдельное согласие на обработку персональных данных");
      }
    }

    return "";
  }

  function toggleWorkCountry(code) {
    const value = String(code || "").toUpperCase();
    setError("");
    setForm((prev) => {
      const current = Array.isArray(prev.workCountries) ? prev.workCountries : [];
      const exists = current.includes(value);
      return {
        ...prev,
        workCountries: exists
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  }

  function nextStep() {
    // Some Android WebView/IME combinations can leave React state one input-event
    // behind when the user taps Continue while a name field is still composing.
    // Blur first, then read the live DOM values and validate that exact snapshot.
    // This keeps the controlled state in sync and prevents the false first-tap error.
    let candidate = form;
    if (STEPS[step] === "person") {
      try { document.activeElement?.blur?.(); } catch {}
      const person = readPersonSnapshot();
      candidate = { ...form, ...person };
      setForm(candidate);
    }

    const message = validateStep(step, candidate);
    if (message) {
      setError(message);
      return;
    }
    setError("");
    setStep((prev) => Math.min(STEPS.length - 1, prev + 1));
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  }

  function prevStep() {
    setError("");
    setStep((prev) => Math.max(0, prev - 1));
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  }

  async function submit() {
    for (let index = 0; index < STEPS.length; index += 1) {
      const message = validateStep(index);
      if (message) {
        setStep(index);
        setError(message);
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const legalV5 = currentLegalBundle || await fetchUzLegalBundle("driver", i18n?.language || "ru");
      let data = registrationCheckpoint?.data || null;
      let outcome = registrationCheckpoint?.outcome || null;

      if (!data || !outcome) {
        data = await registerDriver({
          lastName: String(form.lastName || "").trim(),
          firstName: String(form.firstName || "").trim(),
          middleName: String(form.middleName || "").trim(),
          phoneMain: phoneVerification?.phoneNormalized || form.phoneMain,
          phoneExtra: form.phoneExtra,
          phoneVerificationToken: String(phoneVerification?.verificationToken || ""),
          phoneVerificationCountry: String(phoneVerification?.registrationCountry || "OTHER"),
          workCountries: form.workCountries,
          carrierStatus: form.carrierStatus,
          inn: String(form.inn || "").trim(),
          carrierName: String(form.carrierName || "").trim(),
          car: String(form.car || "").trim(),
          carYear: String(form.carYear || "").trim(),
          carNumber: String(form.carNumber || "").trim(),
          password: form.password,
          ...performerPayload(form),
          agreementAccepted,
          personalDataConsent,
          legalLocale: legalV5.locale,
          presentedBundle: legalV5.presentedBundle,
        });

        outcome =
          resolveRegistrationOutcome(
            data,
            form.performerCategory || "CARGO"
          );
      }

      if (!outcome.driverId) {
        throw new Error("INCOMPLETE_RESPONSE");
      }

      if (outcome.performerCategory === "CARGO") {
        if (!outcome.token) {
          throw new Error("INCOMPLETE_RESPONSE");
        }

        // Persist the authenticated checkpoint before the binary upload so a transient
        // network failure never causes a duplicate /register request on retry.
        localStorage.setItem("driver_id", String(outcome.driverId));
        localStorage.setItem("driver_token", String(outcome.token));
        localStorage.setItem("driver_data", JSON.stringify(outcome.driverSnapshot));

        const checkpoint = {
          data,
          outcome,
          driverId: outcome.driverId,
          token: outcome.token,
          vehiclePhotoUploaded: false,
        };
        setRegistrationCheckpoint(checkpoint);
        saveDriverRegistrationVehiclePhotoCheckpoint(checkpoint);

        if (!registrationCheckpoint?.vehiclePhotoUploaded) {
          const uploaded = await uploadDriverPublicVehiclePhoto(
            outcome.driverId,
            outcome.token,
            vehiclePhotoFile
          );

          const publicVehiclePhoto = String(
            uploaded?.publicVehiclePhoto || uploaded?.url || ""
          ).trim();

          outcome = {
            ...outcome,
            driverSnapshot: {
              ...outcome.driverSnapshot,
              publicVehiclePhoto,
            },
          };

          localStorage.setItem(
            "driver_data",
            JSON.stringify(outcome.driverSnapshot)
          );

          const uploadedCheckpoint = {
            ...checkpoint,
            outcome,
            vehiclePhotoUploaded: true,
          };
          setRegistrationCheckpoint(uploadedCheckpoint);
          saveDriverRegistrationVehiclePhotoCheckpoint(uploadedCheckpoint);
        }
      }

      clearDriverRegistrationResumeState();
      clearDriverRegistrationVehiclePhotoCheckpoint();

      if (outcome.pendingPerformer) {
        // Never leave a stale freight-driver session behind.
        localStorage.removeItem("driver_id");
        localStorage.removeItem("driver_token");
        localStorage.removeItem("driver_data");

        setRegistrationOutcome({
          ...outcome.driverSnapshot,
          assetSubtype:
            data?.driver?.assetSubtype || "",
          bodyTypeOther:
            data?.driver?.bodyTypeOther || "",
        });

        return;
      }

      if (!outcome.token) {
        throw new Error("INCOMPLETE_RESPONSE");
      }

      localStorage.setItem(
        "driver_id",
        String(outcome.driverId)
      );

      localStorage.setItem(
        "driver_token",
        String(outcome.token)
      );

      localStorage.setItem(
        "driver_data",
        JSON.stringify(outcome.driverSnapshot)
      );

      try {
        window.dispatchEvent(
          new Event("gptcargo_driver_auth_changed")
        );
      } catch {}

      onRegistered?.(
        outcome.token,
        outcome.driverSnapshot
      );
    } catch (e) {
      const code = String(e?.message || "REGISTER_FAILED").trim();
      const messages = {
        ALREADY_EXISTS: tr("registerWizard.errors.alreadyExists", "Этот Telegram-номер уже зарегистрирован. Войдите в приложение."),
        DRIVER_EXISTS: tr("registerWizard.errors.alreadyExists", "Этот Telegram-номер уже зарегистрирован. Войдите в приложение."),
        PASSWORD_INVALID: tr("registerWizard.errors.pin", "Придумайте PIN-код из 6 цифр"),
        PASSWORD_FORMAT: tr("registerWizard.errors.pin", "Придумайте PIN-код из 6 цифр"),
        PROFILE_REQUIRED_FIELDS: phase3Text(
          "Проверьте обязательные данные исполнителя и транспорта",
          "Ижрочи ва транспорт ҳақидаги мажбурий маълумотларни текширинг",
          "Uygulayıcı ve araç için zorunlu bilgileri kontrol edin"
        ),
        PERFORMER_CATEGORY_INVALID: phase3Text(
          "Выберите категорию исполнителя",
          "Ижрочи тоифасини танланг",
          "Uygulayıcı kategorisini seçin"
        ),
        ASSET_TYPE_INVALID: phase3Text(
          "Проверьте тип транспорта или техники",
          "Транспорт ёки техника турини текширинг",
          "Araç veya ekipman türünü kontrol edin"
        ),
        SEATS_INVALID: phase3Text(
          "Проверьте количество мест",
          "Ўриндиқлар сонини текширинг",
          "Koltuk sayısını kontrol edin"
        ),
        PASSENGER_SERVICES_REQUIRED: phase3Text(
          "Выберите хотя бы один вид пассажирской услуги",
          "Камида битта йўловчи хизмати турини танланг",
          "En az bir yolcu hizmeti seçin"
        ),
        PASSENGER_SERVICE_INVALID: phase3Text(
          "Проверьте выбранные пассажирские услуги",
          "Танланган йўловчи хизматларини текширинг",
          "Seçilen yolcu hizmetlerini kontrol edin"
        ),
        CAR_YEAR_INVALID: tr("registerWizard.errors.carYear", "Укажите год выпуска четырьмя цифрами"),
        CAPACITY_TONS_INVALID: tr("registerWizard.errors.capacityTons", "Проверьте грузоподъёмность автомобиля"),
        VOLUME_M3_INVALID: tr("registerWizard.errors.volumeM3", "Проверьте объём кузова"),
        VEHICLE_METRICS_REQUIRED: tr("registerWizard.errors.vehicleMetrics", "Укажите тоннаж и объём автомобиля"),
        VEHICLE_PHOTO_REQUIRED: tr("registerWizard.errors.vehiclePhoto", "Добавьте фото транспорта"),
        ONLY_IMAGES: tr("registerWizard.errors.vehiclePhotoType", "Выберите изображение транспорта"),
        LIMIT_FILE_SIZE: tr("registerWizard.errors.vehiclePhotoSize", "Фото транспорта должно быть не больше 25 МБ"),
        DRIVER_ID_MISSING: tr("registerWizard.errors.vehiclePhotoUpload", "Профиль создан, но фото транспорта не загрузилось. Повторите загрузку."),
        DRIVER_AUTH_REQUIRED: tr("registerWizard.errors.vehiclePhotoUpload", "Профиль создан, но фото транспорта не загрузилось. Повторите загрузку."),
        FORBIDDEN_DRIVER_SCOPE: tr("registerWizard.errors.vehiclePhotoUpload", "Профиль создан, но фото транспорта не загрузилось. Повторите загрузку."),
        UPLOAD_ERROR: tr("registerWizard.errors.vehiclePhotoUpload", "Профиль создан, но фото транспорта не загрузилось. Повторите загрузку."),
        LOGIN_REQUIRED: tr("registerWizard.errors.telegramPhone", "Укажите номер телефона, привязанный к Telegram"),
        PHONE_VERIFICATION_REQUIRED: tr("registerWizard.errors.telegramConfirmation", "Подтвердите основной номер через Telegram"),
        PHONE_VERIFICATION_EXPIRED: tr("registerWizard.errors.phoneVerifyExpired", "Подтверждение номера истекло. Повторите проверку через Telegram."),
        PHONE_VERIFICATION_MISMATCH: tr("registerWizard.errors.phoneVerifyMismatch", "Подтверждён другой номер. Повторите проверку через Telegram."),
        PHONE_VERIFICATION_REPLAY: tr("registerWizard.errors.phoneVerifyReplay", "Это подтверждение уже использовано. Подтвердите номер заново."),
        WORK_COUNTRIES_REQUIRED: tr("registerWizard.errors.workCountries", "Выберите хотя бы одну страну, которая вас интересует для новых грузов"),
        BAD_API_BASE: tr("registerWizard.errors.connection", "Приложение не подключено к серверу REISOR"),
        INCOMPLETE_RESPONSE: tr("registerWizard.errors.response", "Сервер вернул неполный ответ"),
      };
      setError(messages[code] || code || tr("registerWizard.errors.connection", "Ошибка соединения с сервером"));
    } finally {
      setLoading(false);
    }
  }

  const stepTitle = [
    phase3Text("Данные исполнителя", "Ижрочи маълумотлари", "Uygulayıcı bilgileri"),
    tr("registerWizard.contactsTitle", "Подтверждение телефона"),
    phase3Text("Что у вас есть?", "Сизда нима бор?", "Hangi aracınız var?"),
    phase3Text("Где вы работаете?", "Қаерда ишлайсиз?", "Nerede çalışıyorsunuz?"),
    tr("registerWizard.accessTitle", "Вход в приложение"),
  ][step];

  const stepHint = [
    phase3Text("Укажите имя и фамилию.", "Исм ва фамилиянгизни киритинг.", "Adınızı ve soyadınızı girin."),
    tr("registerWizard.contactsHintV2", "Подтвердите свой номер через Telegram. Ручное одобрение диспетчера не требуется."),
    phase3Text(
      "Выберите транспорт или технику. REISOR не смешивает разные категории заказов.",
      "Транспорт ёки техникани танланг. REISOR турли буюртма тоифаларини аралаштирмайди.",
      "Araç veya ekipmanı seçin. REISOR farklı iş kategorilerini karıştırmaz."
    ),
    phase3Text(
      "Выберите страны, которые вас интересуют: уведомление придёт, если страна будет местом загрузки или выгрузки.",
      "Сизни қизиқтирадиган давлатларни танланг: давлат юклаш ёки тушириш нуқтаси бўлса, хабар келади.",
      "İlgilendiğiniz ülkeleri seçin: ülke yükleme veya teslimat noktasıysa bildirim gelir."
    ),
    tr("registerWizard.accessHint", "PIN-код должен состоять ровно из 6 цифр."),
  ][step];


  if (registrationOutcome) {
    return (
      <PerformerRegistrationComplete
        outcome={registrationOutcome}
        language={i18n?.language || "ru"}
      />
    );
  }

  return (
    <div
      className="min-h-screen flex justify-center px-4 py-6"
      style={{
        alignItems: "flex-start",
        background: "radial-gradient(circle at center, #0b1e3f, #000814)",
        paddingTop: "max(24px, env(safe-area-inset-top))",
        paddingBottom: "max(24px, env(safe-area-inset-bottom))",
      }}
    >
      <div
        style={{
          maxWidth: 430,
          width: "100%",
          background: "rgba(15, 23, 42, 0.94)",
          padding: 24,
          borderRadius: 28,
          boxShadow: "0 0 60px rgba(0, 122, 255, 0.25)",
        }}
      >
        <h1 style={{ textAlign: "center", fontSize: 24, fontWeight: 900, color: "#60a5fa", marginBottom: 8 }}>
          {phase3Text("Регистрация исполнителя", "Ижрочини рўйхатдан ўтказиш", "Uygulayıcı kaydı")}
        </h1>

        <div style={{ marginBottom: 16 }}><LanguageSelector compact /></div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8, color: "#bfdbfe", fontSize: 13, fontWeight: 800 }}>
          <span>{tr("registerWizard.step", "Шаг {{current}} из {{total}}").replace("{{current}}", String(step + 1)).replace("{{total}}", String(STEPS.length))}</span>
          <span>{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: "rgba(51,65,85,.8)", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ width: `${((step + 1) / STEPS.length) * 100}%`, height: "100%", background: "#2563eb", transition: "width .2s ease" }} />
        </div>

        <div style={{ fontSize: 21, color: "white", fontWeight: 900 }}>{stepTitle}</div>
        <div style={{ ...hintStyle, marginTop: 5, marginBottom: 18 }}>{stepHint}</div>

        {step === 0 ? (
          <>
            <input ref={(node) => { personRefs.current.lastName = node; }} autoFocus autoComplete="family-name" placeholder={tr("register.lastName", "Фамилия")} value={form.lastName} onChange={ch("lastName")} style={inputStyle} />
            <input ref={(node) => { personRefs.current.firstName = node; }} autoComplete="given-name" placeholder={tr("register.firstName", "Имя")} value={form.firstName} onChange={ch("firstName")} style={inputStyle} />
            <input ref={(node) => { personRefs.current.middleName = node; }} autoComplete="additional-name" placeholder={tr("register.middleName", "Отчество — необязательно")} value={form.middleName} onChange={ch("middleName")} style={inputStyle} />
          </>
        ) : null}

        {step === 1 ? (
          <>
            <div style={labelStyle}>{tr("registerWizard.telegramPhone", "Номер телефона, привязанный к Telegram")}</div>
            <input autoFocus inputMode="tel" placeholder="+998 90 123-45-67" value={form.phoneMain} onChange={ch("phoneMain")} style={inputStyle} />
            <div style={hintStyle}>{tr("registerWizard.telegramPhoneHintV2", "Telegram попросит передать ваш собственный контакт. Номер сверяется автоматически и не требует ручного одобрения.")}</div>

            <DriverTelegramPhoneVerification
              phone={form.phoneMain}
              tr={tr}
              beforeOpen={persistRegistrationDraft}
              onStateChange={setPhoneVerification}
            />

            <div style={labelStyle}>{tr("registerWizard.extraPhone", "Дополнительный номер телефона")}</div>
            <input inputMode="tel" placeholder={tr("registerWizard.extraPhonePlaceholder", "Резервный номер — необязательно")} value={form.phoneExtra} onChange={ch("phoneExtra")} style={inputStyle} />
            <div style={hintStyle}>{tr("registerWizard.extraPhoneHint", "Нужен для связи, если Telegram-номер временно недоступен.")}</div>
          </>
        ) : null}

        {step === 2 ? (
          <PerformerAssetStep
            form={form}
            setForm={setForm}
            setError={setError}
            tr={tr}
            language={i18n?.language || "ru"}
            inputStyle={inputStyle}
            labelStyle={labelStyle}
            hintStyle={hintStyle}
            bodyTypeLabel={bodyTypeLabel}
            vehiclePhotoFile={vehiclePhotoFile}
            vehiclePhotoPreview={vehiclePhotoPreview}
            onVehiclePhotoChange={selectVehiclePhoto}
          />
        ) : null}

        {step === 3 ? (
          <div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
              {WORK_COUNTRIES.map(([code, label]) => {
                const active = form.workCountries.includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => toggleWorkCountry(code)}
                    aria-pressed={active}
                    style={{
                      border: active ? "1px solid #3b82f6" : "1px solid #334155",
                      background: active ? "#2563eb" : "rgba(30,41,59,.82)",
                      color: active ? "white" : "#e2e8f0",
                      borderRadius: 999,
                      padding: "10px 13px",
                      fontWeight: 800,
                      fontSize: 14,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div style={{ ...hintStyle, marginTop: 14, marginBottom: 0 }}>
              {tr("registerWizard.countriesSelected", "Выбрано: {{count}}").replace("{{count}}", String(form.workCountries.length))}
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <>
            <input autoFocus type="password" inputMode="numeric" autoComplete="new-password" placeholder={tr("registerWizard.pin", "PIN-код из 6 цифр")} value={form.password} onChange={ch("password")} style={inputStyle} />
            <input type="password" inputMode="numeric" autoComplete="new-password" placeholder={tr("registerWizard.pinRepeat", "Повторите PIN-код")} value={form.password2} onChange={ch("password2")} style={inputStyle} />

            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", color: "#cbd5e1", fontSize: 13, lineHeight: 1.45, margin: "10px 0" }}>
              <input type="checkbox" checked={agreementAccepted} onChange={(e) => { setAgreementAccepted(e.target.checked); setError(""); }} />
              <span>
                {tr("registerWizard.acceptAgreement", "Принимаю пользовательское соглашение, правила перевозчика и правила платформы REISOR")} {" "}
                {[
                  ["REISOR-UA-001", tr("registerWizard.userAgreement", "Пользовательское соглашение")],
                  ["REISOR-RULES-CARRIER-001", tr("registerWizard.carrierRules", "Правила перевозчика")],
                  ["REISOR-PLATFORM-RULES-001", tr("registerWizard.platformRules", "Правила платформы")],
                ].map(([documentNumber, label], index) => (
                  <span key={documentNumber}>
                    {index > 0 ? " · " : ""}
                    <button type="button" onClick={() => openLegalDoc(documentNumber)} style={{ color: "#60a5fa", textDecoration: "underline", background: "none", border: 0, padding: 0 }}>
                      {label}
                    </button>
                  </span>
                ))}.
              </span>
            </label>
            <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.45, margin: "-2px 0 10px 30px" }}>
              {tr("registerWizard.privacyNotice", "Политика конфиденциальности:")} {" "}
              <button type="button" onClick={() => openLegalDoc("REISOR-PRIVACY-001")} style={{ color: "#60a5fa", textDecoration: "underline", background: "none", border: 0, padding: 0 }}>
                {tr("registerWizard.openPrivacy", "открыть текущую редакцию")}
              </button>.
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", color: "#cbd5e1", fontSize: 13, lineHeight: 1.45, margin: "10px 0 16px" }}>
              <input type="checkbox" checked={personalDataConsent} onChange={(e) => { setPersonalDataConsent(e.target.checked); setError(""); }} />
              <span>
                {tr("registerWizard.acceptPdConsent", "Даю отдельное согласие на обработку персональных данных")} {" "}
                <button type="button" onClick={() => openLegalDoc("REISOR-PD-CONSENT-001")} style={{ color: "#60a5fa", textDecoration: "underline", background: "none", border: 0, padding: 0 }}>
                  {tr("registerWizard.openPdConsent", "Открыть согласие")}
                </button>.
              </span>
            </label>
          </>
        ) : null}

        {error ? (
          <div role="alert" style={{ color: "#fecaca", background: "rgba(127,29,29,.28)", border: "1px solid rgba(248,113,113,.45)", borderRadius: 14, padding: "11px 13px", fontSize: 14, lineHeight: 1.4, marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: step > 0 ? "1fr 1.55fr" : "1fr", gap: 10 }}>
          {step > 0 ? (
            <button type="button" onClick={prevStep} disabled={loading} style={{ padding: 14, borderRadius: 16, fontWeight: 900, background: "rgba(148,163,184,.16)", color: "#cbd5e1" }}>
              {tr("registerWizard.back", "Назад")}
            </button>
          ) : null}

          {step < STEPS.length - 1 ? (
            <button type="button" onClick={nextStep} style={{ padding: 14, borderRadius: 16, fontWeight: 900, background: "#2563eb", color: "white" }}>
              {tr("registerWizard.continue", "Продолжить")}
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={loading} style={{ padding: 14, borderRadius: 16, fontWeight: 900, background: "#16a34a", color: "white", opacity: loading ? .65 : 1 }}>
              {loading ? tr("registerWizard.creating", "Создаём аккаунт…") : tr("registerWizard.createV2", "Создать аккаунт REISOR")}
            </button>
          )}
        </div>

        {step === 0 ? (
          <button type="button" onClick={() => { clearDriverRegistrationResumeState(); clearDriverRegistrationVehiclePhotoCheckpoint(); onCancel?.(); }} style={{ width: "100%", padding: 13, marginTop: 10, borderRadius: 16, fontWeight: 800, background: "transparent", color: "#94a3b8" }}>
            {tr("register.back", "Назад ко входу")}
          </button>
        ) : null}

        {selectedLegalDoc ? (
          <LegalDocsModal
            title={selectedLegalDoc.title || selectedLegalDoc.documentNumber || "REISOR"}
            url={legalDocumentUrl(selectedLegalDoc)}
            onClose={() => setSelectedLegalDoc(null)}
          />
        ) : null}
      </div>
    </div>
  );
}
